import { forwardRef } from 'react';
import { Loader2 } from 'lucide-react';

const VARIANTS = {
  primary:
    'bg-white hover:bg-[#64748b] hover:text-white text-[#64748b] shadow-sm hover:shadow-md border border-[#64748b] transition-all duration-200 font-bold',
  secondary:
    'bg-white hover:bg-slate-50 border border-slate-200 text-slate-800 shadow-sm transition-all duration-200 font-semibold',
  orange:
    'bg-[#64748b] hover:bg-[#475569] text-white shadow-sm hover:shadow-[0_4px_15px_rgba(100,116,139,0.25)] border-none transition-all duration-300 font-bold text-xs px-4',
  gold:
    'bg-[#64748b] hover:bg-[#475569] text-white shadow-sm hover:shadow-[0_4px_12px_rgba(100,116,139,0.3)] border border-[#64748b] transition-all duration-200 font-bold tracking-wider uppercase text-[11px]',
  danger:
    'bg-red-600 hover:bg-red-700 text-white shadow-sm transition-all duration-200',
  ghost:
    'text-slate-900 hover:bg-slate-100 shadow-none transition-all duration-200 font-semibold',
  outline:
    'bg-transparent border border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white transition-all duration-200 font-bold',
  'ghost-danger':
    'text-red-600 hover:bg-red-50 shadow-none transition-all duration-200',
};

const SIZES = {
  xs: 'h-7 px-2.5 text-[11px] gap-1 rounded',
  sm: 'h-8 px-3 text-[12px] gap-1.5 rounded-md',
  md: 'h-[35px] px-4 text-[13px] gap-2 rounded-md',
  lg: 'h-10 px-5 text-sm gap-2 rounded-md',
  xl: 'h-12 px-6 text-base gap-2.5 rounded-md',
  icon: 'h-[35px] w-[35px] p-0 rounded-md',
  'icon-sm': 'h-8 w-8 p-0 rounded-md',
  'icon-xs': 'h-7 w-7 p-0 rounded',
};

/**
 * Reusable Button component
 *
 * @param {'primary'|'secondary'|'orange'|'danger'|'ghost'|'outline'|'ghost-danger'} variant
 * @param {'xs'|'sm'|'md'|'lg'|'xl'|'icon'|'icon-sm'|'icon-xs'} size
 * @param {boolean} loading - Shows spinner & disables button
 * @param {boolean} fullWidth - Stretch to fill container
 * @param {React.ReactNode} leftIcon - Icon before text
 * @param {React.ReactNode} rightIcon - Icon after text
 */
const Button = forwardRef(
  (
    {
      children,
      variant = 'primary',
      size = 'md',
      loading = false,
      disabled = false,
      fullWidth = false,
      leftIcon,
      rightIcon,
      className = '',
      type = 'button',
      ...props
    },
    ref,
  ) => {
    const isDisabled = disabled || loading;

    return (
      <button
        ref={ref}
        type={type}
        disabled={isDisabled}
        className={[
          'inline-flex items-center justify-center font-medium',
          'transition-all duration-200 cursor-pointer select-none',
          'active:scale-[0.97] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900',
          'disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100',
          VARIANTS[variant],
          SIZES[size],
          fullWidth && 'w-full',
          className,
        ]
          .filter(Boolean)
          .join(' ')}
        {...props}
      >
        {loading ? (
          <Loader2 size={16} className="animate-spin" />
        ) : (
          leftIcon
        )}
        {children}
        {!loading && rightIcon}
      </button>
    );
  },
);

Button.displayName = 'Button';
export { Button };
export default Button;
